import { LinearGradient } from 'expo-linear-gradient'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { isRelayablyPlanComingSoon } from '@monorepo/relayably/config'
import {
  Activity,
  Apple,
  BarChart3,
  Bell,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Headset,
  Lock,
  MessageSquare,
  Mic2,
  Play,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Volume2,
  X,
  Zap,
} from 'lucide-react-native'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Animated, Platform, Pressable, Switch, Text, useWindowDimensions, View } from 'react-native'
import { FEATURE_ITEMS, OPS_STACK, PILLARS, PROMPTS, SALES_STACK } from '../content'
import { db, handleFirestoreError, hasFirebaseConfig, OperationType } from '../firebase'
import { relayablyPaymentsApi, RelayablyMarketingProduct } from '../paymentsApi'
import { useThemeValue } from '../ThemeContext'
import { baseFont, eyebrow, HEADER_HEIGHT, LeadFormData, monoFont, pricingSubtleColor, pricingTextColor } from '../utils/ui'
import {
  CardGrid,
  Container,
  Field,
  GhostButton,
  PrimaryButton,
  PulseDot,
  ResponsiveSplit,
  Reveal,
  Section,
  SelectField,
  ThemedInput,
  TwoUp,
} from './ui'

export function Hero({ onCtaClick }: { onCtaClick: () => void }) {
  const { theme } = useThemeValue()
  const { width, height } = useWindowDimensions()
  const isMobile = width < 768
  const titleFontSize = width < 420 ? 42 : width < 768 ? 56 : width < 1100 ? 78 : 96
  const titleLineHeight = width < 420 ? 44 : width < 768 ? 58 : width < 1100 ? 80 : 96
  const subtitleFontSize = width < 768 ? 18 : 20
  const subtitleLineHeight = width < 768 ? 28 : 32
  const heroMinHeight = isMobile ? undefined : Math.max(height - HEADER_HEIGHT - 84, 700)
  const gradientTextStyle =
    Platform.OS === 'web'
      ? ({
          backgroundImage: `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.tertiary})`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
        } as const)
      : ({ color: theme.colors.primary } as const)

  return (
    <View
      style={{
        flex: 1,
        minHeight: heroMinHeight,
        paddingTop: isMobile ? 20 : 28,
        paddingBottom: isMobile ? 28 : 36,
        justifyContent: 'space-between',
      }}
    >
      <View style={{ alignItems: 'center', gap: isMobile ? 20 : 24, flex: 1, justifyContent: 'center' }}>
        <Reveal delay={0}>
          <View
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: `${theme.colors.primary}44`,
              backgroundColor: `${theme.colors.primary}18`,
            }}
          >
            <Text style={{ ...baseFont('700'), fontSize: 11, color: theme.colors.primary, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Your calls finally got easier to manage.
            </Text>
          </View>
        </Reveal>
        <Reveal delay={100}>
          <Text
            style={{
              ...baseFont('900'),
              color: theme.colors.text,
              fontSize: titleFontSize,
              lineHeight: titleLineHeight,
              letterSpacing: -3,
              textAlign: 'center',
              maxWidth: 1040,
              paddingHorizontal: isMobile ? 8 : 0,
            }}
          >
            Never miss the right call. <Text style={gradientTextStyle}>Stay available without being interrupted all day.</Text>
          </Text>
        </Reveal>
        <Reveal delay={180}>
          <Text
            style={{
              ...baseFont('500'),
              color: theme.colors.inactive,
              fontSize: subtitleFontSize,
              lineHeight: subtitleLineHeight,
              textAlign: 'center',
              maxWidth: 760,
              paddingHorizontal: isMobile ? 8 : 0,
            }}
          >
            Relayably answers calls, filters out noise, books the right follow-ups, and keeps you in the loop without adding more work.
          </Text>
        </Reveal>
        <Reveal delay={260}>
          <Pressable
            onPress={onCtaClick}
            style={{
              paddingHorizontal: 30,
              paddingVertical: 16,
              borderRadius: 18,
              borderWidth: 1,
              borderColor: theme.colors.border,
              backgroundColor: theme.colors.text,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Text style={{ ...baseFont('700'), color: theme.colors.textReversed, fontSize: 16 }}>Apply for Early Access</Text>
          </Pressable>
        </Reveal>
      </View>

      <Reveal delay={360} offset={10}>
        <View style={{ alignItems: 'center', gap: 20, paddingTop: 32 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={{ width: 28, height: 1, backgroundColor: theme.colors.border }} />
            <Text
              style={{ ...baseFont('700'), color: theme.colors.inactive, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase' }}
            >
              Trusted by 450+ early adopters
            </Text>
            <View style={{ width: 28, height: 1, backgroundColor: theme.colors.border }} />
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 28 }}>
            {['MISSED CALLS', 'SPAM', 'AFTER HOURS', 'INTERRUPTIONS'].map(item => (
              <Text
                key={item}
                style={{
                  ...baseFont('900'),
                  color: theme.colors.text,
                  opacity: 0.18,
                  fontSize: 22,
                  letterSpacing: -0.8,
                  fontStyle: 'italic',
                }}
              >
                {item}
              </Text>
            ))}
          </View>
        </View>
      </Reveal>
    </View>
  )
}

function AppStoreIcon({ color, size }: { color: string; size: number }) {
  return <Apple color={color} fill={color} size={size} strokeWidth={1.5} />
}

export function ProductDemo() {
  const { theme } = useThemeValue()
  const [activeTab, setActiveTab] = useState<'routing' | 'vip' | 'voice'>('routing')
  const floatingY = useRef(new Animated.Value(0)).current
  const progress = useRef(new Animated.Value(0)).current

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(floatingY, { toValue: -10, duration: 2000, useNativeDriver: true }),
        Animated.timing(floatingY, { toValue: 0, duration: 2000, useNativeDriver: true }),
      ])
    ).start()
    Animated.loop(
      Animated.sequence([
        Animated.timing(progress, { toValue: 1, duration: 2000, useNativeDriver: false }),
        Animated.timing(progress, { toValue: 0, duration: 0, useNativeDriver: false }),
      ])
    ).start()
  }, [floatingY, progress])

  const tabs = [
    {
      id: 'routing' as const,
      icon: SlidersHorizontal,
      title: 'Choose What Happens',
      description: 'Decide who gets through, who gets screened, and what happens after hours.',
      color: theme.colors.primary,
    },
    {
      id: 'vip' as const,
      icon: Lock,
      title: 'Priority Callers',
      description: 'Make sure important people can always reach you quickly.',
      color: theme.colors.tertiary,
    },
    {
      id: 'voice' as const,
      icon: Sparkles,
      title: 'Pick a Voice',
      description: 'Choose a voice that sounds right for your business, including an option based on your own voice.',
      color: theme.colors.quarternary,
    },
  ]

  return (
    <Section background={theme.colors.background} paddingTop={96} paddingBottom={96}>
      <Container>
        <View style={{ alignItems: 'center', gap: 16, marginBottom: 48 }}>
          <Reveal>
            <Text
              style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 52, lineHeight: 58, letterSpacing: -2, textAlign: 'center' }}
            >
              Simple by default, <Text style={{ color: theme.colors.primary }}>powerful when needed.</Text>
            </Text>
          </Reveal>
          <Reveal delay={80}>
            <Text
              style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 18, lineHeight: 29, maxWidth: 760, textAlign: 'center' }}
            >
              Start with the basics in minutes, then add more control only when you need it.
            </Text>
          </Reveal>
        </View>
        <ResponsiveSplit
          left={
            <View style={{ gap: 16 }}>
              {tabs.map(tab => (
                <TabCard key={tab.id} active={activeTab === tab.id} onPress={() => setActiveTab(tab.id)} {...tab} />
              ))}
            </View>
          }
          right={<ProductDemoPanel activeTab={activeTab} floatingY={floatingY} progress={progress} />}
        />
      </Container>
    </Section>
  )
}

function TabCard({
  active,
  onPress,
  icon: Icon,
  title,
  description,
  color,
}: {
  active: boolean
  onPress: () => void
  icon: React.ComponentType<any>
  title: string
  description: string
  color: string
}) {
  const { theme } = useThemeValue()
  return (
    <Pressable
      onPress={onPress}
      style={{
        borderRadius: 24,
        padding: 24,
        borderWidth: 1,
        borderColor: active ? theme.colors.border : 'transparent',
        backgroundColor: active ? theme.colors.card : 'transparent',
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 16 }}>
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: 16,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: active ? theme.colors.card : theme.colors.surface,
            borderWidth: 1,
            borderColor: active ? theme.colors.border : 'transparent',
          }}
        >
          <Icon color={active ? color : theme.colors.inactive} size={22} />
        </View>
        <View style={{ flex: 1, gap: 6 }}>
          <Text style={{ ...baseFont('700'), color: active ? theme.colors.text : theme.colors.inactive, fontSize: 18 }}>{title}</Text>
          <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 12, lineHeight: 20 }}>{description}</Text>
        </View>
      </View>
    </Pressable>
  )
}

function ProductDemoPanel({
  activeTab,
  floatingY,
  progress,
}: {
  activeTab: 'routing' | 'vip' | 'voice'
  floatingY: Animated.Value
  progress: Animated.Value
}) {
  const { theme } = useThemeValue()
  const progressWidth = progress.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] })
  const opacity = useRef(new Animated.Value(1)).current

  useEffect(() => {
    opacity.setValue(0)
    Animated.timing(opacity, { toValue: 1, duration: 280, useNativeDriver: true }).start()
  }, [activeTab, opacity])

  const titleMap = { routing: 'Call Handling', vip: 'Priority Contacts', voice: 'Business Voice' }
  const labelMap = { routing: 'R', vip: 'V', voice: 'C' }

  return (
    <View
      style={{
        borderRadius: 40,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor: theme.dark ? 'rgba(0,0,0,0.4)' : theme.colors.surface,
        padding: 32,
        minHeight: 520,
        overflow: 'visible',
      }}
    >
      <View
        style={{
          position: 'absolute',
          right: 24,
          top: 0,
          width: 180,
          height: 180,
          borderRadius: 999,
          backgroundColor: `${theme.colors.primary}22`,
        }}
      />
      <View
        style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 32 }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <LinearGradient
            colors={
              activeTab === 'routing'
                ? [theme.colors.primary, theme.colors.tertiary]
                : activeTab === 'vip'
                ? [theme.colors.tertiary, theme.colors.quarternary]
                : [theme.colors.quarternary, '#f97316']
            }
            style={{ width: 42, height: 42, borderRadius: 14, alignItems: 'center', justifyContent: 'center' }}
          >
            <Text style={{ ...baseFont('700'), color: theme.colors.staticWhite, fontSize: 18 }}>{labelMap[activeTab]}</Text>
          </LinearGradient>
          <View style={{ gap: 4 }}>
            <Text style={{ ...baseFont('700'), color: theme.colors.inactive, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
              Setup Preview
            </Text>
            <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 16 }}>{titleMap[activeTab]}</Text>
          </View>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: `${theme.colors.positive}44`,
            backgroundColor: `${theme.colors.positive}18`,
            paddingHorizontal: 12,
            paddingVertical: 6,
          }}
        >
          <PulseDot color={theme.colors.positive} />
          <Text style={{ ...baseFont('700'), color: theme.colors.positive, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
            Ready
          </Text>
        </View>
      </View>

      <Animated.View style={{ opacity, gap: 16, flex: 1 }}>
        {activeTab === 'routing' ? (
          <>
            {[
              {
                label: 'Priority Contacts',
                icon: Users,
                value: 'Family and top clients',
                description: 'Let them through to you straight away.',
                primary: false,
              },
              {
                label: 'After Hours',
                icon: Clock,
                value: 'After 7:00 PM',
                description: 'Answer the call, take the details, and send you a summary.',
                primary: true,
              },
            ].map((item, index) => {
              const Icon = item.icon
              return (
                <View key={item.label} style={{ paddingLeft: 28, paddingBottom: index === 0 ? 20 : 0 }}>
                  <View
                    style={{
                      position: 'absolute',
                      left: 6,
                      top: 10,
                      bottom: index === 0 ? -20 : undefined,
                      width: 2,
                      backgroundColor: item.primary ? `${theme.colors.primary}33` : theme.colors.border,
                    }}
                  />
                  <View
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 6,
                      width: 14,
                      height: 14,
                      borderRadius: 999,
                      borderWidth: 2,
                      borderColor: item.primary ? theme.colors.primary : theme.colors.border,
                      backgroundColor: item.primary ? theme.colors.primary : theme.colors.background,
                    }}
                  />
                  <View
                    style={{
                      borderRadius: 20,
                      borderWidth: 1,
                      borderColor: item.primary ? `${theme.colors.primary}33` : theme.colors.border,
                      backgroundColor: theme.colors.card,
                      padding: 20,
                      gap: 12,
                    }}
                  >
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                      <Text
                        style={{
                          ...baseFont('700'),
                          color: item.primary ? theme.colors.primary : theme.colors.inactive,
                          fontSize: 10,
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                        }}
                      >
                        {item.label}
                      </Text>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8,
                          paddingHorizontal: 12,
                          paddingVertical: 6,
                          borderRadius: 10,
                          borderWidth: 1,
                          borderColor: theme.colors.border,
                          backgroundColor: theme.colors.surface,
                        }}
                      >
                        <Icon color={theme.colors.inactive} size={14} />
                        <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 12 }}>{item.value}</Text>
                      </View>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <ChevronRight color={theme.colors.primary} size={16} />
                      <Text style={{ ...baseFont('500'), color: theme.colors.text, fontSize: 14 }}>{item.description}</Text>
                    </View>
                  </View>
                </View>
              )
            })}
          </>
        ) : null}

        {activeTab === 'vip' ? (
          <>
            {[
              { name: 'Jane Doe', role: 'Real Estate Director', initial: 'JD', badge: 'Never Screen', active: true },
              { name: 'Tech Support', role: 'Infrastructure Group', initial: 'TS', badge: 'High Priority', active: false },
            ].map(item => (
              <View
                key={item.name}
                style={{
                  borderRadius: 18,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                  backgroundColor: theme.colors.card,
                  padding: 16,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: item.active ? 1 : 0.7,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 999,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${theme.colors.primary}18`,
                    }}
                  >
                    <Text style={{ ...baseFont('700'), color: theme.colors.primary, fontSize: 14 }}>{item.initial}</Text>
                  </View>
                  <View style={{ gap: 4 }}>
                    <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 14 }}>{item.name}</Text>
                    <Text
                      style={{
                        ...baseFont('700'),
                        color: theme.colors.inactive,
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                      }}
                    >
                      {item.role}
                    </Text>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View
                    style={{
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                      borderRadius: 10,
                      borderWidth: 1,
                      borderColor: `${item.active ? theme.colors.primary : theme.colors.tertiary}33`,
                      backgroundColor: `${item.active ? theme.colors.primary : theme.colors.tertiary}16`,
                    }}
                  >
                    <Text
                      style={{
                        ...baseFont('700'),
                        color: item.active ? theme.colors.primary : theme.colors.tertiary,
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                      }}
                    >
                      {item.badge}
                    </Text>
                  </View>
                  {item.active ? <Star size={14} color='#eab308' fill='#eab308' /> : null}
                </View>
              </View>
            ))}
            <View style={{ alignItems: 'center', paddingVertical: 18 }}>
              <Text
                style={{ ...baseFont('700'), color: theme.colors.primary, fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase' }}
              >
                + Add Priority Contact
              </Text>
            </View>
          </>
        ) : null}

        {activeTab === 'voice' ? (
          <View style={{ gap: 16 }}>
            <View
              style={{
                borderRadius: 18,
                borderWidth: 1,
                borderColor: `${theme.colors.primary}33`,
                backgroundColor: `${theme.colors.primary}18`,
                padding: 22,
                gap: 14,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <Mic2 color={theme.colors.primary} size={20} />
                  <View style={{ gap: 4 }}>
                    <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 14, textTransform: 'uppercase' }}>
                      Professional Clone
                    </Text>
                    <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 12, lineHeight: 18 }}>
                      Studio-grade voice replication based on 30min training.
                    </Text>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={{ backgroundColor: theme.colors.primary, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 }}>
                    <Text style={{ ...baseFont('700'), color: theme.colors.staticWhite, fontSize: 9, textTransform: 'uppercase' }}>
                      Active
                    </Text>
                  </View>
                  <ShieldCheck color={theme.colors.primary} size={18} />
                </View>
              </View>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
              <MiniVoiceCard
                title='Instant Clone'
                text='Quick replication using just 15 seconds of voice data.'
                icon={<Volume2 size={16} color={theme.colors.quarternary} />}
              />
              <MiniVoiceCard
                title='Voice Library'
                text='Select from 2,000+ hand-picked professional AI personas.'
                icon={<Star size={16} color='#f97316' />}
              />
            </View>
          </View>
        ) : null}
      </Animated.View>

      <Animated.View
        style={{
          position: 'absolute',
          right: -12,
          bottom: 18,
          width: 280,
          borderRadius: 24,
          borderWidth: 1,
          borderColor: theme.colors.border,
          backgroundColor: theme.colors.background,
          padding: 18,
          transform: [{ translateY: floatingY }],
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${theme.colors.primary}16`,
            }}
          >
            <CheckCircle2 size={16} color={theme.colors.primary} />
          </View>
          <View style={{ gap: 4 }}>
            <Text style={{ ...baseFont('700'), color: theme.colors.inactive, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>
              Operation Success
            </Text>
            <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 12 }}>Rule Updated Across Edge</Text>
          </View>
        </View>
        <View style={{ width: '100%', height: 4, borderRadius: 999, backgroundColor: theme.colors.surface, overflow: 'hidden' }}>
          <Animated.View style={{ width: progressWidth, height: '100%', backgroundColor: theme.colors.primary }} />
        </View>
      </Animated.View>
    </View>
  )
}

function MiniVoiceCard({ title, text, icon }: { title: string; text: string; icon: React.ReactNode }) {
  const { theme } = useThemeValue()
  return (
    <View
      style={{
        flex: 1,
        minWidth: 180,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors.card,
        padding: 16,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        {icon}
        <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 14 }}>{title}</Text>
      </View>
      <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 11, lineHeight: 18 }}>{text}</Text>
    </View>
  )
}

export function MobileAppPreview({ onCtaClick }: { onCtaClick: () => void }) {
  const { theme } = useThemeValue()
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0)
  const [typingText, setTypingText] = useState('')
  const [showLogic, setShowLogic] = useState(false)

  useEffect(() => {
    const fullText = PROMPTS[currentPromptIndex]
    let timer: ReturnType<typeof setTimeout>

    if (typingText.length < fullText.length) {
      timer = setTimeout(() => setTypingText(fullText.slice(0, typingText.length + 1)), 40)
    } else if (!showLogic) {
      timer = setTimeout(() => setShowLogic(true), 3000)
    } else {
      timer = setTimeout(() => {
        setTypingText('')
        setShowLogic(false)
        setCurrentPromptIndex(index => (index + 1) % PROMPTS.length)
      }, 800)
    }

    return () => clearTimeout(timer)
  }, [currentPromptIndex, showLogic, typingText])

  return (
    <Section background={theme.colors.surface} paddingTop={96} paddingBottom={96}>
      <Container>
        <ResponsiveSplit
          left={<AppPreviewCopy onCtaClick={onCtaClick} />}
          right={<PhonePreview typingText={typingText} showLogic={showLogic} />}
        />
      </Container>
    </Section>
  )
}

function AppPreviewCopy({ onCtaClick }: { onCtaClick: () => void }) {
  const { theme } = useThemeValue()
  return (
    <View style={{ gap: 28 }}>
      <Reveal>
        <View style={{ gap: 18 }}>
          <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 48, lineHeight: 56, letterSpacing: -2 }}>
            Set up in minutes.{'\n'}
            <Text style={{ color: theme.colors.primary }}>Stay in control.</Text>
          </Text>
          <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 18, lineHeight: 29 }}>
            Tell Relayably how you want your calls handled, and it takes care of the rest without the usual setup headache.
          </Text>
        </View>
      </Reveal>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 18 }}>
        {[
          { icon: Shield, title: 'Private by design', text: 'Your setup stays clear, simple, and protected.' },
          { icon: Zap, title: 'Changes happen fast', text: 'Update how calls are handled without waiting on a technician.' },
        ].map(item => {
          const Icon = item.icon
          return (
            <View
              key={item.title}
              style={{
                flex: 1,
                minWidth: 220,
                borderRadius: 18,
                borderWidth: 1,
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.card,
                padding: 16,
                flexDirection: 'row',
                gap: 14,
              }}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${theme.colors.primary}16`,
                  borderWidth: 1,
                  borderColor: `${theme.colors.primary}33`,
                }}
              >
                <Icon color={theme.colors.primary} size={18} />
              </View>
              <View style={{ flex: 1, gap: 4 }}>
                <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 13 }}>{item.title}</Text>
                <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 11, lineHeight: 17 }}>{item.text}</Text>
              </View>
            </View>
          )
        })}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        <StoreButton brand='App Store' label='Coming soon to the' icon={AppStoreIcon} primary onPress={onCtaClick} />
        <StoreButton brand='Google Play' label='Coming soon on' icon={Play} onPress={onCtaClick} />
      </View>
    </View>
  )
}

function StoreButton({
  icon: Icon,
  brand,
  label,
  onPress,
  primary = false,
}: {
  icon: React.ComponentType<any>
  brand: string
  label: string
  onPress: () => void
  primary?: boolean
}) {
  const { theme } = useThemeValue()
  return (
    <Pressable
      onPress={onPress}
      style={{
        minWidth: 220,
        flex: 1,
        borderRadius: 18,
        paddingHorizontal: 20,
        paddingVertical: 14,
        borderWidth: primary ? 0 : 1,
        borderColor: theme.colors.border,
        backgroundColor: primary ? theme.colors.text : theme.colors.card,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
      }}
    >
      <Icon color={primary ? theme.colors.textReversed : theme.colors.text} size={22} />
      <View>
        <Text
          style={{
            ...baseFont('900'),
            color: primary ? theme.colors.textReversed : theme.colors.inactive,
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}
        >
          {label}
        </Text>
        <Text style={{ ...baseFont('700'), color: primary ? theme.colors.textReversed : theme.colors.text, fontSize: 18 }}>{brand}</Text>
      </View>
    </Pressable>
  )
}

function PhonePreview({ typingText, showLogic }: { typingText: string; showLogic: boolean }) {
  const { theme } = useThemeValue()
  const cursor = useRef(new Animated.Value(1)).current

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(cursor, { toValue: 0, duration: 500, useNativeDriver: true }),
        Animated.timing(cursor, { toValue: 1, duration: 500, useNativeDriver: true }),
      ])
    ).start()
  }, [cursor])

  return (
    <View style={{ alignItems: 'center', justifyContent: 'center' }}>
      <View
        style={{
          width: 280,
          height: 560,
          borderRadius: 48,
          backgroundColor: '#18181b',
          borderWidth: 8,
          borderColor: '#27272a',
          overflow: 'hidden',
        }}
      >
        <View
          style={{
            position: 'absolute',
            top: 0,
            alignSelf: 'center',
            width: 96,
            height: 20,
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            backgroundColor: '#18181b',
            zIndex: 2,
          }}
        />
        <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
          <View
            style={{
              height: 40,
              paddingHorizontal: 24,
              paddingTop: 8,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 10 }}>9:41</Text>
            <View style={{ width: 16, height: 8, borderRadius: 2, borderWidth: 1, borderColor: theme.colors.border }} />
          </View>
          <View style={{ flex: 1, padding: 20 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 28 }}>
              <Text style={{ ...baseFont('900'), color: theme.colors.text, fontSize: 18, letterSpacing: -1, flex: 1 }}>RELAYABLY</Text>
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.surface,
                }}
              >
                <Settings color={theme.colors.inactive} size={14} />
              </View>
            </View>
            <View style={{ flex: 1, gap: 16 }}>
              <Text
                style={{ ...baseFont('700'), color: theme.colors.inactive, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                Active Intelligence
              </Text>
              <View
                style={{
                  borderRadius: 20,
                  borderWidth: 1,
                  borderColor: `${theme.colors.primary}33`,
                  backgroundColor: `${theme.colors.primary}18`,
                  padding: 16,
                  gap: 8,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <MessageSquare size={14} color={theme.colors.primary} />
                  <Text
                    style={{ ...baseFont('700'), color: theme.colors.primary, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}
                  >
                    Input Feed
                  </Text>
                </View>
                <Text style={{ ...baseFont('500'), color: theme.colors.text, fontSize: 13, lineHeight: 20, minHeight: 80 }}>
                  {typingText}
                  <Animated.Text style={{ opacity: cursor, color: theme.colors.primary }}>{' |'}</Animated.Text>
                </Text>
              </View>
              <Animated.View style={{ opacity: showLogic ? 1 : 0, transform: [{ translateY: showLogic ? 0 : 12 }] }}>
                <Text
                  style={{
                    ...baseFont('700'),
                    color: theme.colors.inactive,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  Generated Logic
                </Text>
                <View
                  style={{
                    borderRadius: 20,
                    borderWidth: 1,
                    borderColor: `${theme.colors.positive}33`,
                    backgroundColor: `${theme.colors.positive}18`,
                    padding: 16,
                    gap: 8,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Check size={14} color={theme.colors.positive} />
                    <Text
                      style={{
                        ...baseFont('700'),
                        color: theme.colors.positive,
                        fontSize: 9,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                      }}
                    >
                      Deployed to Cloud
                    </Text>
                  </View>
                  <Text style={{ ...monoFont(), color: theme.colors.positive, fontSize: 10, lineHeight: 18, textTransform: 'uppercase' }}>
                    IF caller == bank_group{'\n'}&gt; action.ask_department(){'\n'}&gt; action.schedule(next_day)
                  </Text>
                </View>
              </Animated.View>
            </View>
            <View
              style={{
                marginTop: 'auto',
                height: 48,
                borderTopWidth: 1,
                borderTopColor: theme.colors.border,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-around',
                paddingTop: 8,
              }}
            >
              <Smartphone color={theme.colors.primary} size={18} />
              <Bell color={theme.colors.inactive} size={18} />
              <Users color={theme.colors.inactive} size={18} />
            </View>
          </View>
        </View>
      </View>
    </View>
  )
}

export function FeaturesSection({ anchorId }: { anchorId?: string }) {
  const { theme } = useThemeValue()
  return (
    <Section background={theme.colors.background} paddingTop={128} paddingBottom={128} anchorId={anchorId}>
      <Container>
        <View style={{ alignItems: 'center', gap: 16, marginBottom: 56 }}>
          <Text style={eyebrow(theme)}>What Relayably Does</Text>
          <Text
            style={{
              ...baseFont('700'),
              color: theme.colors.text,
              fontSize: 52,
              lineHeight: 58,
              letterSpacing: -2,
              textAlign: 'center',
              maxWidth: 760,
            }}
          >
            Helps you stay responsive without <Text style={{ color: theme.colors.primary }}>being interrupted all day</Text>.
          </Text>
        </View>
        <CardGrid minWidth={280}>
          {FEATURE_ITEMS.map(item => {
            const Icon = item.icon
            return (
              <View
                key={item.title}
                style={{
                  borderRadius: 24,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                  backgroundColor: theme.colors.card,
                  padding: 28,
                  gap: 20,
                  flex: 1,
                  minWidth: 280,
                }}
              >
                <View
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 1,
                    borderColor: theme.colors.border,
                    backgroundColor: theme.colors.surface,
                  }}
                >
                  <Icon color={theme.colors.primary} size={22} />
                </View>
                <View style={{ gap: 10 }}>
                  <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 24, lineHeight: 30 }}>{item.title}</Text>
                  <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 17, lineHeight: 27 }}>{item.description}</Text>
                </View>
              </View>
            )
          })}
        </CardGrid>
        <CardGrid minWidth={280} style={{ marginTop: 36 }}>
          <UseCaseCard
            icon={Users}
            title='Personal'
            dotColor={theme.colors.primary}
            items={['Block all spam calls silently', 'Screen unknown delivery drivers', 'Protect focus and family time']}
          />
          <UseCaseCard
            icon={BarChart3}
            title='Professional'
            dotColor={theme.colors.tertiary}
            items={['Qualify consulting leads', 'Route priority clients', 'Professional 24/7 presence']}
          />
          <UseCaseCard
            icon={Search}
            title='Business'
            badge='Coming Soon'
            inverted
            dotColor={theme.dark ? theme.colors.primary : theme.colors.surface}
            items={['Appointment booking', 'Lead triage & CRM', 'Support overflow']}
          />
        </CardGrid>
      </Container>
    </Section>
  )
}

function UseCaseCard({
  icon: Icon,
  title,
  badge,
  items,
  dotColor,
  inverted = false,
}: {
  icon: React.ComponentType<any>
  title: string
  badge?: string
  items: string[]
  dotColor: string
  inverted?: boolean
}) {
  const { theme } = useThemeValue()
  const backgroundColor = inverted ? (theme.dark ? theme.colors.text : '#18181b') : theme.colors.card
  const textColor = inverted ? (theme.dark ? theme.colors.background : theme.colors.staticWhite) : theme.colors.text
  const itemColor = inverted ? (theme.dark ? 'rgba(9,9,11,0.62)' : 'rgba(255,255,255,0.7)') : theme.colors.inactive

  return (
    <View
      style={{
        flex: 1,
        minWidth: 280,
        borderRadius: 24,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor,
        padding: 28,
        gap: 18,
      }}
    >
      <Icon color={inverted ? (theme.dark ? theme.colors.primary : theme.colors.surface) : dotColor} size={28} />
      <View style={{ gap: 8 }}>
        <Text style={{ ...baseFont('700'), color: textColor, fontSize: 24 }}>{title}</Text>
        {badge ? (
          <View
            style={{
              alignSelf: 'flex-start',
              borderRadius: 999,
              paddingHorizontal: 10,
              paddingVertical: 5,
              backgroundColor: inverted ? 'rgba(255,255,255,0.12)' : `${theme.colors.primary}16`,
              borderWidth: 1,
              borderColor: inverted ? 'rgba(255,255,255,0.14)' : `${theme.colors.primary}22`,
            }}
          >
            <Text
              style={{
                ...baseFont('700'),
                color: inverted ? (theme.dark ? theme.colors.background : theme.colors.staticWhite) : theme.colors.primary,
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              {badge}
            </Text>
          </View>
        ) : null}
      </View>
      <View style={{ gap: 12 }}>
        {items.map(item => (
          <View key={item} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={{ width: inverted ? 8 : 6, height: inverted ? 8 : 6, borderRadius: 999, backgroundColor: dotColor }} />
            <Text style={{ ...baseFont(inverted ? '700' : '500'), color: itemColor, fontSize: 14 }}>{item}</Text>
          </View>
        ))}
      </View>
    </View>
  )
}

export function EnterpriseSection({ anchorId }: { anchorId?: string }) {
  const { theme } = useThemeValue()
  return (
    <Section background={theme.colors.surface} paddingTop={128} paddingBottom={128} anchorId={anchorId}>
      <Container>
        <View style={{ alignItems: 'center', gap: 18, marginBottom: 56 }}>
          <Text style={eyebrow(theme)}>Enterprise Solutions</Text>
          <Text
            style={{
              ...baseFont('700'),
              color: theme.colors.text,
              fontSize: 52,
              lineHeight: 58,
              letterSpacing: -2,
              textAlign: 'center',
              maxWidth: 840,
            }}
          >
            Call handling for <Text style={{ color: theme.colors.primary }}>growing teams</Text>.
          </Text>
          <Text
            style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 20, lineHeight: 32, textAlign: 'center', maxWidth: 820 }}
          >
            Help your team answer more calls, respond faster, and stay organised without adding unnecessary complexity.
          </Text>
        </View>
        <CardGrid minWidth={320}>
          <EnterpriseStack
            title='Sales Teams'
            subtitle='Capture more opportunities without more back-and-forth.'
            color={theme.colors.primary}
            icon={TrendingUp}
            items={SALES_STACK}
          />
          <EnterpriseStack
            title='Support Teams'
            subtitle='Stay responsive as call volume grows.'
            color={theme.colors.tertiary}
            icon={Headset}
            items={OPS_STACK}
          />
        </CardGrid>
      </Container>
    </Section>
  )
}

function EnterpriseStack({
  title,
  subtitle,
  color,
  icon: Icon,
  items,
}: {
  title: string
  subtitle: string
  color: string
  icon: React.ComponentType<any>
  items: string[]
}) {
  const { theme } = useThemeValue()
  return (
    <View
      style={{
        flex: 1,
        minWidth: 320,
        borderRadius: 40,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors.card,
        padding: 36,
        gap: 28,
      }}
    >
      <View
        style={{
          alignSelf: 'flex-end',
          width: 96,
          height: 96,
          borderRadius: 999,
          backgroundColor: `${color}16`,
          position: 'absolute',
          top: 8,
          right: 8,
        }}
      />
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
        <View style={{ width: 56, height: 56, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: color }}>
          <Icon color={theme.colors.staticWhite} size={26} />
        </View>
        <View style={{ gap: 4, flex: 1 }}>
          <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 24 }}>{title}</Text>
          <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 14 }}>{subtitle}</Text>
        </View>
      </View>
      <CardGrid minWidth={180}>
        {items.map(item => (
          <View key={item} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 180, flex: 1 }}>
            <View
              style={{
                width: 20,
                height: 20,
                borderRadius: 999,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `${color}16`,
              }}
            >
              <CheckCircle2 color={color} size={12} />
            </View>
            <Text style={{ ...baseFont('500'), color: theme.colors.text, opacity: 0.74, fontSize: 13, flex: 1 }}>{item}</Text>
          </View>
        ))}
      </CardGrid>
    </View>
  )
}

export function PricingSection({ onCtaClick, anchorId }: { onCtaClick: (plan?: string) => void; anchorId?: string }) {
  const { theme } = useThemeValue()
  const [plans, setPlans] = useState<RelayablyMarketingProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void relayablyPaymentsApi
      .listProducts()
      .then(products => {
        if (cancelled) {
          return
        }

        setPlans(products)
      })
      .catch(error => {
        if (cancelled) {
          return
        }

        setErrorMessage(error instanceof Error ? error.message : 'Relayably could not load pricing right now.')
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Section background={theme.colors.background} paddingTop={96} paddingBottom={96} anchorId={anchorId}>
      <Container>
        <View style={{ alignItems: 'center', gap: 16, marginBottom: 56 }}>
          <Text
            style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 44, lineHeight: 48, letterSpacing: -2, textAlign: 'center' }}
          >
            Believable early access pricing.
          </Text>
          <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 18, textAlign: 'center' }}>
            Secure early adopter rates before our public launch.
          </Text>
        </View>
        {loading ? (
          <View style={{ alignItems: 'center', paddingVertical: 48 }}>
            <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 16 }}>Loading pricing…</Text>
          </View>
        ) : errorMessage ? (
          <View style={{ alignItems: 'center', gap: 12, paddingVertical: 48 }}>
            <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 22, textAlign: 'center' }}>Pricing unavailable</Text>
            <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 16, lineHeight: 26, textAlign: 'center', maxWidth: 620 }}>
              {errorMessage}
            </Text>
          </View>
        ) : (
        <CardGrid minWidth={260}>
          {plans.map(plan => {
            const comingSoon = isRelayablyPlanComingSoon(plan.id)

            return (
            <View
              key={plan.name}
              style={{
                flex: 1,
                minWidth: 260,
                borderRadius: 40,
                borderWidth: 1,
                borderColor: plan.highlight ? (theme.dark ? theme.colors.background : theme.colors.text) : theme.colors.border,
                backgroundColor: plan.highlight ? (theme.dark ? theme.colors.text : '#18181b') : theme.colors.card,
                padding: 28,
                gap: 26,
                transform: [{ scale: plan.highlight ? 1.03 : 1 }],
                opacity: comingSoon ? 0.72 : 1,
              }}
            >
              {plan.highlight ? (
                <View
                  style={{
                    position: 'absolute',
                    top: -14,
                    alignSelf: 'center',
                    backgroundColor: theme.colors.primary,
                    paddingHorizontal: 16,
                    paddingVertical: 6,
                    borderRadius: 999,
                  }}
                >
                  <Text
                    style={{
                      ...baseFont('700'),
                      color: theme.colors.staticWhite,
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                    }}
                  >
                    Most Popular
                  </Text>
                </View>
              ) : null}
              <View style={{ gap: 12 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Text style={{ ...baseFont('700'), color: pricingTextColor(theme, plan.highlight), fontSize: 20 }}>{plan.name}</Text>
                  {comingSoon ? (
                    <View
                      style={{
                        borderRadius: 999,
                        paddingHorizontal: 10,
                        paddingVertical: 5,
                        backgroundColor: plan.highlight ? 'rgba(255,255,255,0.12)' : `${theme.colors.inactive}14`,
                        borderWidth: 1,
                        borderColor: plan.highlight ? 'rgba(255,255,255,0.16)' : `${theme.colors.inactive}18`,
                      }}
                    >
                      <Text
                        style={{
                          ...baseFont('700'),
                          color: pricingSubtleColor(theme, plan.highlight),
                          fontSize: 10,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        Coming Soon
                      </Text>
                    </View>
                  ) : null}
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 4 }}>
                  <Text style={{ ...baseFont('700'), color: pricingTextColor(theme, plan.highlight), fontSize: 36, letterSpacing: -1.4 }}>
                    {plan.displayPrice}
                  </Text>
                  {plan.displayPrice !== 'Custom' ? (
                    <Text style={{ ...baseFont('500'), color: pricingSubtleColor(theme, plan.highlight), fontSize: 14, marginBottom: 6 }}>
                      /{plan.interval}
                    </Text>
                  ) : null}
                </View>
                <Text style={{ ...baseFont('500'), color: pricingSubtleColor(theme, plan.highlight), fontSize: 14, lineHeight: 22 }}>
                  {plan.description}
                </Text>
              </View>
              <View style={{ gap: 14, flex: 1 }}>
                {plan.features.map(feature => (
                  <View key={feature} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <View
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 999,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: `${theme.colors.primary}33`,
                      }}
                    >
                      <Check color={theme.colors.primary} size={12} />
                    </View>
                    <Text style={{ ...baseFont('500'), color: pricingTextColor(theme, plan.highlight), fontSize: 14, flex: 1 }}>
                      {feature}
                    </Text>
                  </View>
                ))}
                {plan.note ? (
                  <Text
                    style={{
                      ...baseFont('700'),
                      color: theme.colors.primary,
                      fontSize: 14,
                      lineHeight: 22,
                      paddingTop: 6,
                    }}
                  >
                    {plan.note}
                  </Text>
                ) : null}
              </View>
              <Pressable
                disabled={comingSoon}
                onPress={() => {
                  if (comingSoon) {
                    return
                  }

                  onCtaClick(plan.name)
                }}
                style={{
                  paddingVertical: 16,
                  borderRadius: 18,
                  backgroundColor: plan.highlight ? theme.colors.primary : theme.dark ? theme.colors.background : theme.colors.text,
                  opacity: comingSoon ? 0.5 : 1,
                }}
              >
                <Text
                  style={{
                    ...baseFont('700'),
                    color: theme.colors.staticWhite,
                    fontSize: 14,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}
                >
                  {comingSoon ? 'Coming Soon' : plan.cta || `Choose ${plan.name}`}
                </Text>
              </Pressable>
            </View>
            )
          })}
        </CardGrid>
        )}
        <Text
          style={{
            ...baseFont('700'),
            color: theme.colors.inactive,
            fontSize: 10,
            marginTop: 34,
            letterSpacing: 1,
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
        >
          * Early access pricing subject to change. High-volume teams can contact us for enterprise quotes.
        </Text>
      </Container>
    </Section>
  )
}

export function HomeNarrative() {
  const { theme } = useThemeValue()
  return (
    <Section background={theme.colors.surface} paddingTop={128} paddingBottom={128} anchorId='vision'>
      <Container>
        <Reveal>
          <View style={{ alignItems: 'center', gap: 22, marginBottom: 52 }}>
            <Text style={eyebrow(theme)}>Why Relayably Exists</Text>
            <Text
              style={{
                ...baseFont('700'),
                color: theme.colors.text,
                fontSize: 56,
                lineHeight: 62,
                letterSpacing: -2,
                textAlign: 'center',
                maxWidth: 920,
              }}
            >
              To help people and businesses keep up when{' '}
              <Text style={{ color: theme.colors.primary }}>communication becomes too much.</Text>
            </Text>
            <Text
              style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 20, lineHeight: 32, textAlign: 'center', maxWidth: 780 }}
            >
              Calls, messages, spam, enquiries, and interruptions are piling up. Relayably helps you stay available without carrying all
              of that yourself.
            </Text>
          </View>
        </Reveal>

        <View
          style={{
            borderRadius: 24,
            borderWidth: 1,
            borderColor: theme.colors.border,
            backgroundColor: theme.colors.background,
            padding: 28,
            gap: 24,
          }}
        >
          <ResponsiveSplit
            left={
              <View style={{ gap: 18 }}>
                <Text style={eyebrow(theme)}>The Problem</Text>
                <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 42, lineHeight: 48, letterSpacing: -1.8 }}>
                  Communication is becoming overwhelming.
                </Text>
                <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 18, lineHeight: 29 }}>
                  As AI makes it easier to send more messages and make more calls, people and businesses are already struggling to keep up.
                </Text>
              </View>
            }
            right={
              <View
                style={{
                  borderRadius: 20,
                  borderWidth: 1,
                  borderColor: `${theme.colors.primary}22`,
                  backgroundColor: `${theme.colors.primary}12`,
                  padding: 24,
                  gap: 14,
                }}
              >
                <Text style={eyebrow(theme)}>Our Mission</Text>
                <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 30, lineHeight: 36, letterSpacing: -1.4 }}>
                  To create a world where communication respects human attention.
                </Text>
                <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 16, lineHeight: 26 }}>
                  Make call handling simple, affordable, and accessible for the people who need it most.
                </Text>
              </View>
            }
          />
        </View>
      </Container>
    </Section>
  )
}

export function HomeInsight() {
  const { theme } = useThemeValue()
  return (
    <Section background={theme.colors.background} paddingTop={96} paddingBottom={96}>
      <Container>
        <Reveal>
          <View style={{ alignItems: 'center', gap: 20 }}>
            <Text
              style={{ ...baseFont('500'), color: theme.colors.text, fontSize: 28, lineHeight: 40, textAlign: 'center', maxWidth: 840 }}
            >
              "The real value is helping busy people stay responsive without giving up their time and focus."
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <View style={{ width: 48, height: 1, backgroundColor: theme.colors.border }} />
              <Text
                style={{ ...baseFont('700'), color: theme.colors.inactive, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.4 }}
              >
                Relayably Core Insight
              </Text>
              <View style={{ width: 48, height: 1, backgroundColor: theme.colors.border }} />
            </View>
          </View>
        </Reveal>
      </Container>
    </Section>
  )
}

export function VisionPillars() {
  const { theme } = useThemeValue()
  return (
    <View style={{ marginTop: 112 }}>
      <Text style={eyebrow(theme)}>Strategic Pillars</Text>
      <CardGrid minWidth={300} style={{ marginTop: 36 }}>
        {PILLARS.map((pillar, index) => {
          const Icon = pillar.icon
          return (
            <Reveal key={pillar.title} delay={index * 70}>
              <View
                style={{
                  flex: 1,
                  minWidth: 300,
                  borderRadius: 24,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                  backgroundColor: theme.colors.card,
                  padding: 32,
                  gap: 20,
                }}
              >
                <View
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${theme.colors.primary}18`,
                  }}
                >
                  <Icon color={theme.colors.primary} size={28} />
                </View>
                <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 24, lineHeight: 30 }}>{pillar.title}</Text>
                <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 16, lineHeight: 27 }}>{pillar.description}</Text>
              </View>
            </Reveal>
          )
        })}
      </CardGrid>
    </View>
  )
}
